import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlertBar from '../../components/alert-bar';
import dayjs from 'dayjs';
import {
  ButtonWithAction,
  ButtonWithLink,
  Box,
  BoxWithShadow,
  Center,
  Cluster,
  constants,
  DefaultPageTemplate,
  Loading,
  Modal,
  Paragraph,
  Spinner,
  SolidDivider,
  Text,
  Title,
  Module,
  util,
} from '@thecb/components';
import { OrderDetailsPaymentItem } from './OrderDetailsPaymentItem';
import { OrderDetailsDebtItem } from './OrderDetailsDebtItem';
import {
  formatPrice,
  phoneNumberWithoutCountryCode,
} from '../../utils/helpers';
import { ReceiptFormModule } from '../../components/receipt-form-module';
import { StatusModule } from '../../components/status-module/StatusModule';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeDueModal,
  CLOSE_CHANGE_DUE_MODAL,
  initiateReopenOrderById,
  initiateReturnOrderById,
  initiateVoidOrderById,
  initiateCancelPaylink,
  voidModal,
  returnModal,
  OPEN_VOID_MODAL,
  OPEN_RETURN_MODAL,
  CLOSE_VOID_MODAL,
  CLOSE_RETURN_MODAL,
} from './OrderDetails.state';
import {
  ORDER_STATUS,
  TRANSACTION_STATUS,
  PAYMENT_TYPE,
} from '../../utils/constants';
import Tooltip from '../../components/tooltip';
import { submitCancelOrderRequest } from '../cart/Cart.state';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';
import { useLocation } from 'react-router-dom';
import { VoidForm } from './OrderDetailsVoidModal';
import { ReturnForm } from './OrderDetailsReturnModal';
import { format } from 'formatted-input';

const Header = ({ alertBar, alertBarActions }) => {
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  return (
    <Box width="100%" padding="16px 0">
      <Center maxWidth="85.714rem">
        {alertBar.alerts.length > 0 && (
          <Box padding="16px 0">
            <AlertBar isToast={true} {...alertBar} {...alertBarActions} />
          </Box>
        )}
        {sharedAlerts.length > 0 && (
          <Box padding="16px 0">
            <SharedAlertBar isToast={true} />
          </Box>
        )}
      </Center>
    </Box>
  );
};

const OrderDetails = ({
  fetching,
  alertBars,
  changeDue,
  actions,
  modals,
  forms,
  getOrderDetails,
  error,
  errorText,
  submitSendReceipt,
  submitPrintReceipt,
}) => {
  const {
    data: selectedOrder,
    isLoading,
    isVoidModalOpen,
    isReturnModalOpen,
    isChangeDueModalOpen,
  } = useSelector((state) => state.orderDetails);
  const { order: cartOrder } = useSelector((state) => state.cart);
  const {
    order_items,
    // pushed activity log feature to V2
    // activity_log: activityLog,
    transaction_details: transactionDetails,
  } = selectedOrder;
  const location = useLocation();
  const navigate = useNavigate();
  const voidFormActions = actions.forms.voidForm.actions;
  const voidFormFields = forms.voidForm.fields;
  const returnFormActions = actions.forms.returnForm.actions;
  const returnFormFields = forms.returnForm.fields;
  const dispatch = useDispatch();
  const { id } = useParams();
  // Receipt form error handler
  const [errors, handleErrors] = useState(false);
  const [showReceiptSubsection, setShowReceiptSubsection] = useState(false);
  useEffect(() => {
    getOrderDetails({ id });
  }, [location]);
  const orderContainsCashPayments = useMemo(
    () =>
      transactionDetails?.some(
        (transaction) =>
          transaction?.payment_type === PAYMENT_TYPE.CASH_PAYMENT &&
          transaction?.status === TRANSACTION_STATUS.SUCCESSFUL,
      ),
    [transactionDetails],
  );
  const voidText = 'Void';
  const returnText = 'Refund';

  const getVoidButtonText = () =>
    orderContainsCashPayments ? 'Return Cash and Void' : 'Void';

  const disableReopenButton =
    !selectedOrder?.order?.cashier_can_reopen || cartOrder?.order_items?.length;
  const disableReopenButtonText = cartOrder?.order_items?.length
    ? 'Cannot re-open held order if items are already in your cart. Please clear cart.'
    : selectedOrder?.order?.cashier_can_reopen_message;

  const ShowTooltip = ({ show, text, children }) => (
    <Fragment>
      {show ? (
        <Tooltip
          width="275px"
          height="95%"
          variant="error"
          left
          noIcon
          text={text}
        >
          {children}
        </Tooltip>
      ) : (
        <Fragment>{children}</Fragment>
      )}
    </Fragment>
  );
  const isRefund = !!selectedOrder.order?.parent_id;
  const isVoidable = selectedOrder?.order?.order_settings?.voidable;
  const isReturnable = selectedOrder?.order?.order_settings?.returnable;
  const isVoidedOrder = Boolean(selectedOrder?.order?.voided_at);
  const orderStatus = useSelector(
    (state) => state.orderDetails.data.order?.status,
  );
  const isPaylinkSent =
    selectedOrder?.order?.status === ORDER_STATUS.PAYLINK_SENT;

  /*The isPaylinkCancelled is a boolean value in the state, 
  from the Cancel webhook being successful or failing. 
  This helps rerenders the component so the button is no longer visible. 
  The paylinkCancelledAt is the time stamp for fresh loads, 
  to control the button being visible.
  */
  const isPaylinkCancelled = useSelector(
    (state) => state.orderDetails.isPaylinkCancelled,
  );
  const paylinkCancelledAt = Boolean(
    selectedOrder?.order?.paylink_cancelled_at,
  );

  useEffect(() => {
    if (isPaylinkCancelled) {
      dispatch(getOrderDetails({ id }));
    }
  }, [isPaylinkCancelled, id]);

  useEffect(() => {
    if (orderStatus === ORDER_STATUS.VOIDED || isPaylinkCancelled) {
      dispatch(getOrderDetails({ id }));
    }
  }, [orderStatus]);

  const total = formatPrice(selectedOrder.order?.total, isRefund);
  const subtotal = formatPrice(selectedOrder.order?.subtotal, isRefund);
  const paylinkRecipientEmail = selectedOrder.order?.paylink_recipient_email;
  const formatPhone = format(util.formats.phoneFormat);
  const formatPaylinkRecipientPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return;

    const paylinkRecipientPhoneNumberWithoutCountryCode =
      phoneNumberWithoutCountryCode(phoneNumber);

    return formatPhone(paylinkRecipientPhoneNumberWithoutCountryCode);
  };
  const paylinkRecipientPhoneNumber = formatPaylinkRecipientPhoneNumber(
    selectedOrder.order?.paylink_recipient_phone_number,
  );
  const paylinkRecipientInfo =
    paylinkRecipientEmail || paylinkRecipientPhoneNumber;

  const orderDetailsModals = () => (
    <Fragment>
      <Modal
        noBorder
        modalHeaderText="Change Due"
        defaultWrapper={false}
        onlyCloseButton={true}
        hideModal={() => dispatch(changeDueModal(CLOSE_CHANGE_DUE_MODAL))}
        modalOpen={isChangeDueModalOpen}
        modalBodyText={
          <Center intrinsic>
            <Text variant="p">There is change due in the amount of:</Text>
            <Title
              as="h3"
              margin="8px 0px 0px 0px"
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            >
              {formatPrice(changeDue)}
            </Title>
          </Center>
        }
      />
      <Modal
        modalHeaderText="Processing"
        defaultWrapper={false}
        onlyCloseButton={true}
        modalOpen={
          modals.loadingModal.modalIsOpen &&
          modals.loadingModal.modalId === LOADING_MODAL
        }
        hideModal={actions.modals.loadingModal.toggleModal}
        modalBodyText={
          <Center intrinsic>
            <Box>
              <Spinner size="100" />
            </Box>
          </Center>
        }
      />
      <Modal
        modalHeaderText={`${voidText} Order`}
        defaultWrapper={false}
        continueAction={() => {
          const payload = {
            notes: voidFormFields.notes.rawValue,
            id: selectedOrder.order.id,
          };
          dispatch(initiateVoidOrderById(payload));
        }}
        isLoading={isLoading}
        isContinueActionDisabled={
          isLoading || forms.voidForm.fields.notes.hasErrors
        }
        continueButtonText={getVoidButtonText()}
        modalOpen={isVoidModalOpen}
        hideModal={() => dispatch(voidModal(CLOSE_VOID_MODAL))}
        modalBodyText={
          <Box>
            <VoidForm
              fields={voidFormFields}
              actions={voidFormActions}
              transactionsForOrder={transactionDetails}
              orderContainsCashPayments={orderContainsCashPayments}
            />
          </Box>
        }
      />
      <Modal
        modalHeaderText={`${returnText} Order`}
        defaultWrapper={false}
        continueAction={() => {
          const payload = {
            notes: returnFormFields.notes.rawValue,
            id: selectedOrder.order.id,
            navigate,
          };
          dispatch(initiateReturnOrderById(payload));
        }}
        isLoading={isLoading}
        isContinueActionDisabled={
          isLoading || forms.returnForm.fields.notes.hasErrors
        }
        continueButtonText={returnText}
        modalOpen={isReturnModalOpen}
        hideModal={() => dispatch(returnModal(CLOSE_RETURN_MODAL))}
        modalBodyText={
          <Box>
            <ReturnForm fields={returnFormFields} actions={returnFormActions} />
          </Box>
        }
      />
    </Fragment>
  );

  const buttonStyle = `
    border-radius: 4px;
    font-size: 1rem;
  `;
  const disabledBtnContainerStyle = `
    cursor: not-allowed;
    margin-left: -8px;
  `;
  const disabledBtnStyle = `
    pointer-events: none;
    border-color: ${constants.colors.GREY_CHATEAU} !important;
    color: ${constants.colors.GREY_CHATEAU} !important;
    background-color: ${constants.colors.ALABASTER_WHITE} !important;
  `;
  const disabledBtnTextStyle = `
    color: ${constants.colors.STORM_GREY} !important; 
    background-color: ${constants.colors.ALABASTER_WHITE} !important;
    font-size: 1rem;
  `;

  const orderActions = () => (
    <Cluster align="center" overflow="visible">
      {selectedOrder?.order?.status === ORDER_STATUS.HOLD && (
        <ShowTooltip show={disableReopenButton} text={disableReopenButtonText}>
          <Box
            padding="0"
            extraStyles={disableReopenButton && disabledBtnContainerStyle}
          >
            <ButtonWithAction
              data-testid="Re-open Button"
              text="Re-open order"
              isLoading={false}
              variant="secondary"
              action={() => dispatch(initiateReopenOrderById({ id, navigate }))}
              disabled={disableReopenButton}
              extraStyles={`${buttonStyle};
                ${disableReopenButton && disabledBtnStyle}
                `}
              textExtraStyles={
                disableReopenButton ? disabledBtnTextStyle : `font-size: 1rem;`
              }
            />
          </Box>
        </ShowTooltip>
      )}
      {(selectedOrder.order.status === ORDER_STATUS.COMPLETED ||
        selectedOrder.order.status === ORDER_STATUS.VOIDED ||
        selectedOrder.order.status === ORDER_STATUS.VOID_PENDING ||
        selectedOrder.order.completed_at) && (
        <ButtonWithAction
          id="receipt-btn"
          disabled={showReceiptSubsection}
          extraStyles={buttonStyle}
          text="Receipt"
          isLoading={false}
          action={() => setShowReceiptSubsection(!showReceiptSubsection)}
          textExtraStyles="font-size: 1rem;"
        />
      )}
      {!isRefund && (
        <>
          {/* Void */}
          {isVoidable && (
            <Box padding="0">
              <ButtonWithAction
                extraStyles={buttonStyle}
                data-testid={`${voidText} Button`}
                text={voidText}
                isLoading={false}
                variant="secondary"
                textExtraStyles="font-size: 1rem;"
                action={() => dispatch(voidModal(OPEN_VOID_MODAL))}
              />
            </Box>
          )}
          {/* Return */}
          {isReturnable && (
            <Box padding="0">
              <ButtonWithAction
                extraStyles={buttonStyle}
                data-testid={`${returnText} Button`}
                text={returnText}
                isLoading={false}
                variant="secondary"
                textExtraStyles="font-size: 1rem;"
                action={() => dispatch(returnModal(OPEN_RETURN_MODAL))}
              />
            </Box>
          )}
        </>
      )}
      {isPaylinkSent && !paylinkCancelledAt && (
        <ButtonWithAction
          id="cancel-paylink-btn"
          extraStyles={buttonStyle}
          text="Cancel Paylink"
          isLoading={false}
          variant="secondary"
          textExtraStyles="font-size: 1rem;"
          action={() => {
            dispatch(
              initiateCancelPaylink({
                id: selectedOrder.order.paylink_id,
                order_id: id,
              }),
            );
          }}
        />
      )}
      {selectedOrder.order.items_with_artifacts && (
        <ButtonWithAction
          extraStyles={buttonStyle}
          text="Re-Print Artifact"
          isLoading={false}
          variant="secondary"
          textExtraStyles="font-size: 1rem;"
        />
      )}
      {[ORDER_STATUS.OPENED, ORDER_STATUS.INCOMPLETE].includes(
        selectedOrder.order.status,
      ) && (
        <ButtonWithLink
          extraStyles={buttonStyle}
          url="/pos/cart"
          text="Go to cart"
          isLoading={false}
          variant="secondary"
          textExtraStyles="font-size: 1rem;"
        />
      )}
      {[ORDER_STATUS.OPENED, ORDER_STATUS.HOLD].includes(
        selectedOrder.order.status,
      ) &&
        (transactionDetails.length === 0 ||
          transactionDetails.every(
            (detail) => detail.status === TRANSACTION_STATUS.CANCELLED,
          )) && (
          <ButtonWithAction
            extraStyles={buttonStyle}
            textExtraStyles="font-size: 1rem;"
            text="Cancel order"
            isLoading={false}
            variant="secondary"
            action={() =>
              dispatch(
                submitCancelOrderRequest({
                  id: selectedOrder.order.id,
                  navigate,
                }),
              )
            }
          />
        )}
    </Cluster>
  );

  const body =
    error || Boolean(!selectedOrder.order) ? (
      <Box>
        <Paragraph>Error</Paragraph>
        <Paragraph>Sorry, {errorText}</Paragraph>
      </Box>
    ) : (
      <Fragment>
        {orderDetailsModals()}
        <Box padding="1rem 0">
          <BoxWithShadow
            variant="baseStandard"
            padding="0"
            borderRadius=".25rem"
          >
            <Box
              background={constants.colors.ATHENS_GREY}
              borderRadius=".25rem"
            >
              <Cluster justify="space-between" align="center">
                <Title
                  as="h4"
                  variant="small"
                  color={constants.colors.CHARADE_GREY}
                  weight="600"
                >
                  Order Number:{' '}
                  <Text
                    weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                    fontSize="20px"
                  >
                    {selectedOrder.order.id}
                  </Text>
                </Title>
                <Paragraph
                  fontSize="20px"
                  weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                  id="order-total"
                >
                  {total}
                </Paragraph>
              </Cluster>
            </Box>
            <SolidDivider />
            <Box background={constants.colors.WHITE} borderRadius=".25rem">
              <Cluster justify="space-between" align="center">
                <StatusModule
                  label="Order"
                  status={selectedOrder.order.status}
                />
                {orderActions()}
              </Cluster>
            </Box>
            {showReceiptSubsection && (
              <ReceiptFormModule
                forms={forms}
                actions={actions}
                buttonStyle={buttonStyle}
                handleErrors={handleErrors}
                showErrors={errors}
                submitSendReceipt={submitSendReceipt}
                submitPrintReceipt={submitPrintReceipt}
                cancelAction={() => setShowReceiptSubsection(false)}
              />
            )}
          </BoxWithShadow>
        </Box>

        {/* ORDER INFO SECTION */}
        <Box padding="1rem 0">
          <Title
            as="h4"
            variant="small"
            color={constants.colors.CHARADE_GREY}
            weight="600"
            extraStyles={`margin-bottom: 0.5rem;`}
          >
            Order Details
          </Title>
          <BoxWithShadow
            variant="baseStandard"
            padding="0"
            background={constants.colors.WHITE}
            borderRadius=".25rem"
          >
            <Box padding="1.5rem" width="550px" borderRadius=".25rem">
              <Cluster extraStyles={`text-align:left`}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Order creation:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph fontSize="1rem" id="formatted-created-at">
                    {dayjs(selectedOrder.order.created_at).format(
                      'MM/DD/YYYY hh:mmA',
                    )}
                  </Paragraph>
                </Box>
              </Cluster>
              <Cluster extraStyles={`text-align:left`}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Order completion:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph fontSize="1rem" id="formatted-completed-at">
                    {selectedOrder.order.completed_at
                      ? dayjs(selectedOrder.order.completed_at).format(
                          'MM/DD/YYYY hh:mmA',
                        )
                      : 'Incomplete'}
                  </Paragraph>
                </Box>
              </Cluster>
              {isVoidedOrder && (
                <Cluster extraStyles={`text-align:left`}>
                  <Box padding="0" extraStyles={`width:30%`}>
                    <Paragraph
                      fontSize="1rem"
                      weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                    >
                      Order voided:
                    </Paragraph>
                  </Box>
                  <Box padding="0" extraStyles={`width:50%`}>
                    <Paragraph fontSize="1rem" id="formatted-voided-at">
                      {dayjs(selectedOrder.order.voided_at).format(
                        'MM/DD/YYYY hh:mmA',
                      )}
                    </Paragraph>
                  </Box>
                </Cluster>
              )}
              <Cluster extraStyles={`text-align:left`}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Cashier:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph
                    fontSize="1rem"
                    extraStyles={`text-align:left`}
                    id="cashier"
                  >
                    {selectedOrder.order.cashier}
                  </Paragraph>
                </Box>
              </Cluster>
              <Cluster extraStyles={`text-align:left`}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Location:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph fontSize="1rem" id="location">
                    {selectedOrder.order.location}
                  </Paragraph>
                </Box>
              </Cluster>
              <Cluster extraStyles={`text-align:left`}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Terminal:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph fontSize="1rem" id="terminal">
                    {selectedOrder.order.terminal}
                  </Paragraph>
                </Box>
              </Cluster>
              <Cluster extraStyles={`text-align:left;`} overflow={true}>
                <Box padding="0" extraStyles={`width:30%`}>
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                  >
                    Batch Number:
                  </Paragraph>
                </Box>
                <Box padding="0" extraStyles={`width:50%`}>
                  <Paragraph fontSize="1rem" id="batch-number">
                    {selectedOrder.order.batch_number}
                  </Paragraph>
                </Box>
              </Cluster>
              {paylinkRecipientInfo && (
                <Cluster extraStyles={`text-align:left;`} overflow={true}>
                  <Box padding="0" extraStyles={`width:30%`}>
                    <Paragraph
                      fontSize="1rem"
                      weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                    >
                      PayLink Recipient:
                    </Paragraph>
                  </Box>
                  <Box padding="0">
                    <Paragraph fontSize="1rem" id="paylink-recipient-info">
                      {paylinkRecipientEmail && paylinkRecipientPhoneNumber
                        ? `${paylinkRecipientEmail}, ${paylinkRecipientPhoneNumber}`
                        : `${paylinkRecipientInfo}`}
                    </Paragraph>
                  </Box>
                </Cluster>
              )}
            </Box>
            <SolidDivider />
            <Box
              background={constants.colors.ATHENS_GREY}
              padding="0 1.5rem"
              borderRadius=".25rem"
            >
              <Box padding="1.5rem 0">
                {parseFloat(selectedOrder.order.total_service_fees) > 0 && (
                  <Cluster
                    justify="space-between"
                    extraStyles={`margin-bottom: 0.5rem;`}
                  >
                    <Paragraph
                      fontSize="1rem"
                      weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                    >
                      Service Fees
                    </Paragraph>
                    <Paragraph
                      fontSize="1rem"
                      id="order-details-total-service-fees"
                    >
                      {formatPrice(selectedOrder.order.total_service_fees)}
                    </Paragraph>
                  </Cluster>
                )}
                {parseFloat(selectedOrder.order.total_convenience_fees) > 0 && (
                  <Cluster
                    justify="space-between"
                    extraStyles={`margin-bottom: 0.5rem;`}
                  >
                    <Paragraph
                      fontSize="1rem"
                      weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                    >
                      Transaction Fee
                    </Paragraph>
                    <Paragraph
                      fontSize="1rem"
                      id="order-details-total-convenience-fees"
                    >
                      {formatPrice(selectedOrder.order.total_convenience_fees)}
                    </Paragraph>
                  </Cluster>
                )}
                <Cluster justify="space-between">
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                  >
                    Subtotal
                  </Paragraph>
                  <Paragraph fontSize="1rem" id="order-details-subtotal">
                    {subtotal}
                  </Paragraph>
                </Cluster>
              </Box>
              <SolidDivider />
              <Box padding="1.5rem 0">
                <Cluster justify="space-between">
                  <Paragraph
                    fontSize="1rem"
                    weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                  >
                    Total
                  </Paragraph>
                  <Paragraph
                    fontSize="1.125rem"
                    weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                    id="order-details-total"
                  >
                    {total}
                  </Paragraph>
                </Cluster>
              </Box>
            </Box>
          </BoxWithShadow>
        </Box>
        <Module heading="Debt Details" variant="small">
          <Box
            padding="1.5rem"
            background={constants.colors.WHITE}
            borderRadius=".25rem"
          >
            {order_items &&
              order_items.map((order_item, key) => (
                <OrderDetailsDebtItem
                  key={`${order_item.category}-${key}`}
                  index={key}
                  header={order_item.category}
                  subheader={order_item.description}
                  paid={order_item.amount}
                  isNegativeAmount={order_item.is_negative_amount}
                  isRefund={isRefund}
                  fullAmount={order_item.total}
                  externalAttributes={order_item.receipt_visible_attributes}
                  quantity={order_item.quantity}
                  recordedPrice={order_item.recorded_price}
                />
              ))}
          </Box>
        </Module>
        <Module heading="Payment Method Details" variant="small">
          <Box
            padding="0"
            background={constants.colors.WHITE}
            borderRadius=".25rem"
          >
            <Box
              background={constants.colors.WHITE}
              padding="0"
              borderRadius=".25rem"
              id="payment-method-details-container"
            >
              {(!Boolean(transactionDetails) ||
                transactionDetails.length === 0) && (
                <Box>
                  <Paragraph fontSize="1rem">No payments to display.</Paragraph>
                </Box>
              )}
              {transactionDetails.map((transaction, index) => (
                <Fragment key={transaction.id}>
                  {index >= 1 && <SolidDivider />}
                  <OrderDetailsPaymentItem
                    transactionData={transaction}
                    index={index}
                    isRefund={isRefund}
                  />
                </Fragment>
              ))}
            </Box>
          </Box>
        </Module>
      </Fragment>
    );

  return (
    <DefaultPageTemplate
      maxWidth="85.714rem"
      header={
        <Header
          key="OrderDetailsHeader"
          alertBar={alertBars.orderDetailsAlertBars}
          alertBarActions={actions.alertBars.orderDetailsAlertBars}
        />
      }
      gutters="0"
      content={fetching ? <Loading /> : body}
    />
  );
};

export default OrderDetails;
